import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { canManageKarkhana } from "../../helpers/karkhanaEnabled";
import {
  approveRateVersion,
  discardRateVersion,
  duplicateRateCard,
  getRateCard,
  getRateCardAudit,
  listKarkhanaWorkTypes,
  saveRateDraft,
  startRateVersion,
  updateRateCard,
} from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import { DesignPicker } from "./KarkhanaRateCardsPage";
import {
  changedFields,
  DraftRow,
  fmtDay,
  perInch,
  RateLine,
  RateVersion,
  rupees,
  toDraftRows,
  toLineInputs,
  todayIso,
  versionHistory,
  versionLabel,
} from "./rates/rateModel";
import { useKT } from "./i18n/kt";
import "./workers/karkhana-workers.scss";
import "./rates/karkhana-rates.scss";
import PageHeader from "./ui/PageHeader";
import { confirmDialog } from "./ui/dialog";
import { kp } from "./karkhanaPaths";
import { useKarkhanaTitle } from "./ui/addressState";

const readRole = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

const FIELD_LABEL: Record<string, string> = {
  weaverRatePerInch: "weaver ₹/inch",
  sourceExpression: "typed as",
  aloneAllowancePerInch: "alone ₹/inch",
  ladkaSoolPerInch: "ladka sool ₹/inch",
  dailyTargetInches: "daily target",
  cutoffPercent: "cutoff %",
  bonusPercent: "bonus %",
  effectiveFrom: "start date",
  rateGroup: "group",
};

const ACTION_LABEL: Record<string, string> = {
  CREATE: "Card created",
  DRAFT_SAVED: "Draft saved",
  NEW_VERSION: "New version started",
  APPROVED: "Approved",
  CLOSED: "Ended",
  DISCARDED: "Draft discarded",
  DUPLICATED_FROM: "Created as a copy",
  DUPLICATED_TO: "Copied to another design",
  CARD_UPDATED: "Card updated",
};

const call = async (fn: () => Promise<any>, ok?: string): Promise<any | null> => {
  try {
    const res: any = await fn();
    if (!res?.success) {
      notifications({ title: "Error", message: res?.message || "Something went wrong", type: "danger" });
      return null;
    }
    if (ok) notifications({ title: "Done", message: ok, type: "success" });
    return res.data;
  } catch {
    notifications({ title: "Error", message: "Could not reach the server", type: "danger" });
    return null;
  }
};

const VERSION_STATUS: Record<string, string> = { APPROVED: "Approved", DRAFT: "Draft", DISCARDED: "Discarded" };

const KarkhanaRateCardPage = () => {
  const { t } = useKT();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const role = readRole();
  const canWrite = canManageKarkhana(role) && role !== "SUPER_ADMIN";
  const [card, setCard] = useState<any>(null);
  const [workTypes, setWorkTypes] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [rows, setRows] = useState<DraftRow[]>([]);
  const [from, setFrom] = useState("");
  const [note, setNote] = useState("");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState<any[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [modal, setModal] = useState<"" | "approve" | "newVersion" | "duplicate" | "group">("");
  const [newFrom, setNewFrom] = useState(todayIso());
  const [target, setTarget] = useState<{ id: string; number: string } | null>(null);
  const [groupText, setGroupText] = useState("");

  const versions: RateVersion[] = useMemo(() => card?.versions || [], [card]);
  const selected = versions.find((v) => v.id === selectedId) || null;
  const isDraft = selected?.status === "DRAFT";
  const girah = Number(card?.inchesPerGirah || 2.6875);
  const current = versions.find((v) => v.id === card?.currentVersionId);
  const baseLines = new Map<string, RateLine>((current?.lines || []).map((l) => [l.workTypeId, l]));

  const applyCard = useCallback((data: any, keepSelection?: string) => {
    setCard(data);
    const vs: RateVersion[] = data?.versions || [];
    const pick =
      vs.find((v) => v.id === keepSelection && v.status !== "DISCARDED") ||
      vs.find((v) => v.status === "DRAFT") ||
      vs.find((v) => v.id === data?.currentVersionId) ||
      vs.find((v) => v.status !== "DISCARDED");
    setSelectedId(pick?.id || "");
  }, []);

  const loadAudit = useCallback(async () => {
    const res: any = await getRateCardAudit(id).catch(() => null);
    setAudit(res?.success ? res.data || [] : []);
  }, [id]);

  useEffect(() => {
    if (!canManageKarkhana(role)) {
      history.replace(kp.home());
      return;
    }
    (async () => {
      const [c, w]: any[] = await Promise.all([getRateCard(id), listKarkhanaWorkTypes(0, 200)]);
      if (!c?.success) {
        notifications({ title: "Error", message: c?.message || "Rate card not found", type: "danger" });
        history.replace(kp.rateCards());
        return;
      }
      setWorkTypes(w?.success ? w.data?.docs || [] : []);
      applyCard(c.data);
      loadAudit();
    })();
  }, [id, role, history, applyCard, loadAudit]);

  // Reset the editable grid whenever another version is selected or reloaded.
  useEffect(() => {
    if (!selected) return;
    setRows(toDraftRows(workTypes, selected.lines, Number(card?.inchesPerGirah || 2.6875)));
    setFrom(selected.effectiveFrom);
    setNote(selected.note || "");
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, workTypes]);

  const setCell = (i: number, patch: Partial<DraftRow>) => {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    setDirty(true);
  };

  const after = async (data: any, keep?: string) => {
    if (data) {
      applyCard(data, keep);
      loadAudit();
    }
  };

  const save = async (quiet = false) => {
    if (!selected) return null;
    setBusy(true);
    try {
      const data = await call(
        () => saveRateDraft(id, selected.id, { effectiveFrom: from, note, lines: toLineInputs(rows) }),
        quiet ? undefined : "Draft saved"
      );
      await after(data, selected.id);
      return data;
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    if (!selected) return;
    if (dirty && !(await save(true))) return;
    setBusy(true);
    try {
      const data = await call(() => approveRateVersion(id, selected.id), `Version ${selected.versionNo} approved`);
      setModal("");
      if (data?.repriced) {
        notifications({ title: t("Pay worked out again"), message: t("{{n}} entries now use these rates.", { n: data.repriced }), type: "info" });
      }
      await after(data);
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    if (!selected || !(await confirmDialog({ title: `Discard draft v${selected.versionNo}? Approved rates stay as they are.` }))) return;
    setBusy(true);
    try {
      await after(await call(() => discardRateVersion(id, selected.id), "Draft discarded"));
    } finally {
      setBusy(false);
    }
  };

  const startNew = async () => {
    setBusy(true);
    try {
      const data = await call(() => startRateVersion(id, { effectiveFrom: newFrom }), "New draft started from the latest rates");
      setModal("");
      await after(data);
    } finally {
      setBusy(false);
    }
  };

  const duplicate = async () => {
    if (!target) return;
    setBusy(true);
    try {
      const data = await call(() => duplicateRateCard(id, { targetDesignId: target.id }), `Copied to ${target.number}`);
      setModal("");
      if (data) history.push(kp.rateCard(data.id));
    } finally {
      setBusy(false);
    }
  };

  const saveGroup = async () => {
    setBusy(true);
    try {
      const data = await call(() => updateRateCard(id, { rateGroup: groupText }), "Group saved");
      setModal("");
      await after(data, selectedId);
    } finally {
      setBusy(false);
    }
  };

  useKarkhanaTitle(card?.designNumber ? `Rate card ${card.designNumber}` : null);
  if (!canManageKarkhana(role)) return null;
  if (!card) {
    return (
      <div className="gi-registry-page kw-page">
        <div className="kw-empty">Loading…</div>
      </div>
    );
  }

  const shownRows = rows
    .map((r, i) => ({ r, i }))
    .filter(({ r }) => isDraft || showAll || r.rate || r.alone || r.sool || r.target || r.cutoff || r.bonus);
  const filled = rows.filter((r) => r.rate).length;

  return (
    <div className="gi-registry-page kw-page">
      <PageHeader title="Rate card" back={kp.rateCards()} backLabel="All rate cards" />

      <div className="kr-head">
        <h3 className="kw-code">{card.designNumber}</h3>
        {card.designName && card.designName !== card.designNumber ? <span className="kw-muted">{card.designName}</span> : null}
        <span className="kw-chip">{card.rateGroup ? `Group: ${card.rateGroup}` : "No group"}</span>
        {canWrite ? (
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            onClick={() => {
              setGroupText(card.rateGroup || "");
              setModal("group");
            }}
          >
            Change group
          </button>
        ) : null}
        {card.copiedFromCardId ? (
          <span className="kw-muted">
            Copied from{" "}
            <Link to={kp.rateCard(card.copiedFromCardId)} className="kw-code">
              {card.copiedFromDesignNumber}
            </Link>
          </span>
        ) : null}
      </div>

      <div className="kr-versions" role="tablist" aria-label="Versions">
        {versions
          .filter((v) => v.status !== "DISCARDED")
          .map((v) => (
            <button
              key={v.id}
              type="button"
              role="tab"
              aria-selected={v.id === selectedId}
              className={[
                "kr-version",
                v.id === selectedId ? "is-active" : "",
                v.id === card.currentVersionId ? "is-current" : "",
                v.status === "DRAFT" ? "is-draft" : "",
              ].join(" ")}
              onClick={async () => {
                if (dirty && !(await confirmDialog({ title: "You have unsaved changes. Leave them?" }))) return;
                setSelectedId(v.id);
              }}
            >
              {versionLabel(v)}
              {v.id === card.currentVersionId ? " · in use" : ""}
            </button>
          ))}
      </div>

      {selected ? (
        <>
          {isDraft ? (
            <div className="kw-card" style={{ marginBottom: 12 }}>
              <div className="kw-toolbar" style={{ marginBottom: 0 }}>
                <div>
                  <Label for="kr-from" className="kw-muted">
                    Rates apply from
                  </Label>
                  <Input
                    id="kr-from"
                    type="date"
                    value={from}
                    disabled={!canWrite}
                    onChange={(e) => {
                      setFrom(e.target.value);
                      setDirty(true);
                    }}
                  />
                </div>
                <div style={{ flex: "1 1 260px" }}>
                  <Label for="kr-note" className="kw-muted">
                    Note (optional)
                  </Label>
                  <Input
                    id="kr-note"
                    value={note}
                    disabled={!canWrite}
                    placeholder="e.g. 2026 rates agreed with weavers"
                    onChange={(e) => {
                      setNote(e.target.value);
                      setDirty(true);
                    }}
                  />
                </div>
              </div>
              <p className="kw-muted mt-2" style={{ margin: 0 }}>
                {t("Any start date works: approved versions around it are adjusted, and work already entered in its dates is paid again at these rates.")}
              </p>
            </div>
          ) : (
            <p className="kw-lede">
              {selected.effectiveTo
                ? `Used from ${fmtDay(selected.effectiveFrom)} to ${fmtDay(selected.effectiveTo)}.`
                : `In use from ${fmtDay(selected.effectiveFrom)}.`}
              {selected.approvedByName ? ` Approved by ${selected.approvedByName}.` : ""} Approved rates can't be
              changed; start a new version for new rates.
              {selected.note ? ` Note: ${selected.note}` : ""}
            </p>
          )}

          <div className="kr-table-wrap">
            <table className="kr-table">
              <thead>
                <tr>
                  <th>Work type</th>
                  <th>Weaver rate</th>
                  <th className="kr-num">₹ per inch</th>
                  <th>Alone allowance ₹/inch</th>
                  <th title={t("Paid on top of the rate only when the entry says ladka sool")}>{t("Ladka sool extra")}</th>
                  <th>Daily target (in)</th>
                  <th>Cutoff %</th>
                  <th>Bonus %</th>
                </tr>
              </thead>
              <tbody>
                {shownRows.length === 0 ? (
                  <tr className="is-empty">
                    <td colSpan={8}>No rates in this version.</td>
                  </tr>
                ) : (
                  shownRows.map(({ r, i }) => {
                    const pi = perInch(r.rate, r.rateUnit, girah);
                    const changed = isDraft ? changedFields(r, baseLines.get(r.workTypeId), girah) : new Set<string>();
                    const bad = !!r.rate && pi === null;
                    const empty = !r.rate && !r.alone && !r.sool && !r.target;
                    const editable = isDraft && canWrite;
                    const cls = (f: string) =>
                      ["kr-input", changed.has(f) && current ? "is-changed" : "", f === "rate" && bad ? "is-bad" : ""].join(" ");
                    return (
                      <tr key={r.workTypeId} className={empty ? "is-empty" : ""}>
                        <td>
                          <span className="kw-code">{r.code}</span> {r.name}
                        </td>
                        <td>
                          {editable ? (
                            <div className="kr-cell">
                              <input
                                className={cls("rate")}
                                value={r.rate}
                                inputMode="decimal"
                                placeholder="—"
                                aria-label={`${r.code} weaver rate`}
                                onChange={(e) => setCell(i, { rate: e.target.value })}
                              />
                              <select
                                className="kr-unit"
                                value={r.rateUnit}
                                aria-label={`${r.code} rate unit`}
                                onChange={(e) => setCell(i, { rateUnit: e.target.value as "INCH" | "GIRAH" })}
                              >
                                <option value="INCH">per inch</option>
                                <option value="GIRAH">per girah</option>
                              </select>
                            </div>
                          ) : (
                            <span className="kw-code">
                              {r.rate ? `${r.rate} ${r.rateUnit === "GIRAH" ? "per girah" : "per inch"}` : "—"}
                            </span>
                          )}
                        </td>
                        <td className="kr-num">{bad ? <span className="kw-chip is-review">Check</span> : rupees(pi)}</td>
                        <td>
                          {editable ? (
                            <input className={cls("alone")} value={r.alone} inputMode="decimal" placeholder="—"
                              aria-label={`${r.code} alone allowance`} onChange={(e) => setCell(i, { alone: e.target.value })} />
                          ) : (
                            rupees(r.alone || null)
                          )}
                        </td>
                        <td>
                          {editable ? (
                            <input className={cls("sool")} value={r.sool} inputMode="decimal" placeholder="—"
                              aria-label={`${r.code} ${t("Ladka sool extra")}`} onChange={(e) => setCell(i, { sool: e.target.value })} />
                          ) : r.sool ? (
                            `${rupees(perInch(r.sool, r.rateUnit, girah))}/in`
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          {editable ? (
                            <input className={cls("target")} value={r.target} inputMode="decimal" placeholder="—"
                              aria-label={`${r.code} daily target inches`} onChange={(e) => setCell(i, { target: e.target.value })} />
                          ) : (
                            r.target || "—"
                          )}
                        </td>
                        <td>
                          {editable ? (
                            <input className={cls("cutoff")} value={r.cutoff} inputMode="decimal" placeholder="—"
                              aria-label={`${r.code} cutoff percent`} onChange={(e) => setCell(i, { cutoff: e.target.value })} />
                          ) : (
                            r.cutoff ? `${r.cutoff}%` : "—"
                          )}
                        </td>
                        <td>
                          {editable ? (
                            <input className={cls("bonus")} value={r.bonus} inputMode="decimal" placeholder="—"
                              aria-label={`${r.code} bonus percent`} onChange={(e) => setCell(i, { bonus: e.target.value })} />
                          ) : (
                            r.bonus ? `${r.bonus}%` : "—"
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <p className="kw-muted mt-2">
            {isDraft
              ? `Type ₹ per inch, ₹ per girah, or “500/10.5” for ₹500 per 10.5 units. 1 girah = ${girah} inches. ${
                  current ? "Yellow cells differ from the rates in use today. " : ""
                }The incentive needs all three numbers, or none. `
              : null}
            {isDraft ? t("Ladka sool extra is in the same unit as the rate, paid only on entries marked ladka sool.") : null}
            {!isDraft ? (
              <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setShowAll(!showAll)}>
                {showAll ? "Hide work types without rates" : "Show all work types"}
              </button>
            ) : null}
          </p>

          {canWrite ? (
            <div className="kr-actions">
              {isDraft ? (
                <>
                  <span className="kw-muted">
                    Draft v{selected.versionNo} · {filled} rate{filled === 1 ? "" : "s"}
                    {dirty ? " · unsaved changes" : ""}
                  </span>
                  <span className="kr-spacer" />
                  <Button size="sm" outline disabled={busy} onClick={discard}>
                    Discard draft
                  </Button>
                  <Button size="sm" outline disabled={busy || !dirty} onClick={() => save()}>
                    Save draft
                  </Button>
                  {card.canApprove ? (
                    <Button size="sm" className="ResetBtn" disabled={busy || !filled} onClick={() => setModal("approve")}>
                      Approve…
                    </Button>
                  ) : (
                    <span className="kw-chip is-info">The studio admin approves rates</span>
                  )}
                </>
              ) : (
                <>
                  <span className="kr-spacer" />
                  <Button
                    size="sm"
                    outline
                    disabled={busy}
                    onClick={() => {
                      setTarget(null);
                      setModal("duplicate");
                    }}
                  >
                    Copy to another design
                  </Button>
                  <Button
                    size="sm"
                    className="ResetBtn"
                    disabled={busy}
                    onClick={() => {
                      setNewFrom(todayIso());
                      setModal("newVersion");
                    }}
                  >
                    New version
                  </Button>
                </>
              )}
            </div>
          ) : null}
        </>
      ) : (
        <div className="kw-empty">This card has no versions.</div>
      )}

      <div className="kw-card" style={{ marginTop: 16 }}>
        <h3>{t("Rate versions")}</h3>
        <p className="kw-muted">{t("Each version pays work done between its dates. Drafts pay nothing until approved.")}</p>
        <ul className="kr-history">
          {versionHistory(versions).map((v) => (
            <li key={v.id}>
              <strong>v{v.versionNo}</strong>{" "}
              <span className={`kw-chip${v.status === "APPROVED" ? " is-ok" : " is-review"}`}>{t(VERSION_STATUS[v.status] || v.status)}</span>{" "}
              {v.effectiveTo
                ? t("{{from}} to {{to}}", { from: fmtDay(v.effectiveFrom), to: fmtDay(v.effectiveTo) })
                : v.status === "APPROVED"
                  ? t("from {{from}}", { from: fmtDay(v.effectiveFrom) })
                  : t("would start {{from}}", { from: fmtDay(v.effectiveFrom) })}
              {v.id === card.currentVersionId ? <span className="kw-muted"> · {t("in use today")}</span> : null}
            </li>
          ))}
        </ul>
      </div>

      <div className="kw-card" style={{ marginTop: 16 }}>
        <h3>History</h3>
        {audit.length === 0 ? (
          <p className="kw-muted">Nothing yet.</p>
        ) : (
          <ul className="kr-history">
            {audit.map((a) => (
              <li key={a.id}>
                <strong>{ACTION_LABEL[a.action] || a.action}</strong>
                {a.versionNo ? <span className="kw-muted"> · v{a.versionNo}</span> : null}
                <span className="kw-muted">
                  {" "}
                  · {a.byName} · {new Date(a.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                </span>
                {a.note ? <div className="kw-muted">{a.note}</div> : null}
                {(a.changes || []).slice(0, 12).map((c: any, k: number) => (
                  <span key={k} className="kr-change">
                    {c.workTypeCode ? `${c.workTypeCode} ` : ""}
                    {FIELD_LABEL[c.field] || c.field}: {c.from ?? "—"} → {c.to ?? "—"}
                  </span>
                ))}
                {(a.changes || []).length > 12 ? (
                  <span className="kr-change">and {a.changes.length - 12} more</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      <Modal isOpen={modal === "approve"} toggle={() => setModal("")} modalClassName="kw-modal">
        <ModalHeader toggle={() => setModal("")}>Approve v{selected?.versionNo}?</ModalHeader>
        <ModalBody>
          <p>
            These rates will be used for work from <strong>{fmtDay(from)}</strong>. {t("HKP fits them among the approved versions and says what changed.")}
          </p>
          <p className="kw-muted" style={{ marginBottom: 0 }}>
            After approval these rates can't be edited. To change them later, start a new version.
            {dirty ? " Your unsaved changes will be saved first." : ""}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal("")}>
            Cancel
          </Button>
          <Button className="ResetBtn" disabled={busy} onClick={approve}>
            {busy ? "Approving…" : "Approve rates"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={modal === "newVersion"} toggle={() => setModal("")} modalClassName="kw-modal">
        <ModalHeader toggle={() => setModal("")}>New version</ModalHeader>
        <ModalBody>
          <p className="kw-muted">
            {t("The rates in force on that day are copied into a draft for you to change. A date before the first approved version adds older rates.")}
          </p>
          <Label for="kr-new-from">New rates apply from</Label>
          <Input id="kr-new-from" type="date" value={newFrom} onChange={(e) => setNewFrom(e.target.value)} />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal("")}>
            Cancel
          </Button>
          <Button className="ResetBtn" disabled={busy || !newFrom} onClick={startNew}>
            Start draft
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={modal === "duplicate"} toggle={() => setModal("")} modalClassName="kw-modal">
        <ModalHeader toggle={() => setModal("")}>Copy to another design</ModalHeader>
        <ModalBody>
          <p className="kw-muted">
            Makes a new rate card for the other design with these rates as a draft. It gets its own ID and remembers it
            was copied from {card.designNumber}.
          </p>
          <Label>Design</Label>
          <DesignPicker value={target} onChange={setTarget} excludeIds={[card.designId]} />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal("")}>
            Cancel
          </Button>
          <Button className="ResetBtn" disabled={busy || !target} onClick={duplicate}>
            Copy rates
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={modal === "group"} toggle={() => setModal("")} modalClassName="kw-modal">
        <ModalHeader toggle={() => setModal("")}>Group</ModalHeader>
        <ModalBody>
          <p className="kw-muted">Groups help find cards with similar rates. Leave blank for none.</p>
          <Input value={groupText} onChange={(e) => setGroupText(e.target.value)} placeholder="e.g. Silk tanchui" />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal("")}>
            Cancel
          </Button>
          <Button className="ResetBtn" disabled={busy} onClick={saveGroup}>
            Save
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default KarkhanaRateCardPage;
